/**
 * Search utility functions for standardized search handling across components
 */

/**
 * Safely converts a value to a searchable string
 * @param {any} value - The value to convert
 * @returns {string} - The normalized string value
 */
export const normalizeSearchValue = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.toLowerCase().trim();
  if (typeof value === 'number') return value.toString().toLowerCase();
  if (typeof value === 'boolean') return value.toString().toLowerCase();
  return String(value).toLowerCase().trim();
};

/**
 * Checks if a normalized value contains the search query
 * @param {any} value - The value to search in
 * @param {string} searchQuery - The search query (already lowercased)
 * @returns {boolean} - Whether the value matches the search
 */
export const matchesSearchQuery = (value, searchQuery) => {
  if (!searchQuery) return true;
  const normalizedValue = normalizeSearchValue(value);
  return normalizedValue.includes(searchQuery.toLowerCase().trim());
};

/**
 * Creates a search filter function for projects
 * @param {string} searchQuery - The search query
 * @returns {function} - Filter function for projects
 */
export const createProjectSearchFilter = (searchQuery) => {
  // Use exact same pattern as createClientSearchFilter which works
  if (!searchQuery || searchQuery.trim() === '') {
    return () => true;
  }

  const query = searchQuery.toLowerCase().trim();

  return (project) => {
    // Define all searchable fields for projects
    const searchableFields = [
      project.projectName,
      project.jobNumber,
      project.clientName,
      project.modelPrice,
      project.price,
      project.poNumber,
      project.contactName,
      project.modelingStatus,
      project.billingStatus,
      project.agreementStatus,
      // Include revision if it exists
      project.rev ? `Rev ${project.rev}` : null,
      // Include combined job number with revision
      project.jobNumber && project.rev ? `${project.jobNumber} Rev ${project.rev}` : null
    ];

    // Check if any field matches the search query
    return searchableFields.some(field => matchesSearchQuery(field, query));
  };
};

/**
 * Creates a search filter function for clients
 * @param {string} searchQuery - The search query
 * @returns {function} - Filter function for clients
 */
export const createClientSearchFilter = (searchQuery) => {
  if (!searchQuery || searchQuery.trim() === '') {
    return () => true;
  }

  const query = searchQuery.toLowerCase().trim();

  return (client) => {
    // Define all searchable fields for clients
    const searchableFields = [
      client.name,
      client.address,
      client.state,
      client.company_phone,
      client.client_of,
      client.billing_status,
      client.client_status,
      client.yearly_cost
    ];

    // Check if any field matches the search query
    return searchableFields.some(field => matchesSearchQuery(field, query));
  };
};

/**
 * Creates a search filter function for takeoffs
 * @param {string} searchQuery - The search query
 * @returns {function} - Filter function for takeoffs
 */
export const createTakeoffSearchFilter = (searchQuery) => {
  // Use exact same pattern as createClientSearchFilter which works
  if (!searchQuery || searchQuery.trim() === '') {
    return () => true;
  }

  const query = searchQuery.toLowerCase().trim();

  return (takeoff) => {
    // Define all searchable fields for takeoffs
    const searchableFields = [
      takeoff.projectName,
      takeoff.jobNumber,
      takeoff.formClient,
      takeoff.earthworksTechInvNumber,
      takeoff.status,
      takeoff.invoicedStatus
    ];

    // Check if any field matches the search query
    return searchableFields.some(field => matchesSearchQuery(field, query));
  };
};

/**
 * Highlights search matches in text (returns plain text for now, can be enhanced for JSX)
 * @param {string} text - The text to highlight in
 * @param {string} searchQuery - The search query
 * @returns {string} - The text (future: could return JSX with highlighting)
 */
export const highlightSearchMatch = (text, searchQuery) => {
  // For now, just return the original text
  // This can be enhanced later to return JSX with <mark> tags
  return text;
};

/**
 * Counts the number of search results
 * @param {Array} items - The array of items to count
 * @param {string} searchQuery - The search query
 * @param {function} filterFunction - The filter function to apply
 * @returns {number} - The count of matching items
 */
export const getSearchResultCount = (items, searchQuery, filterFunction) => {
  if (!searchQuery || searchQuery.trim() === '') {
    return items.length;
  }

  return items.filter(filterFunction(searchQuery)).length;
};

/**
 * Validates if a search query is meaningful (not just whitespace)
 * @param {string} searchQuery - The search query to validate
 * @returns {boolean} - Whether the query is meaningful
 */
export const isValidSearchQuery = (searchQuery) => {
  return searchQuery && searchQuery.trim().length > 0;
};